import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { WORKSPACE_ROLE } from '@/types/models'
import type { User, Workspace, WorkspaceRole } from '@/types/models'

export const useSessionStore = defineStore('session', () => {
  const isLoading = ref(false)
  const user = ref<User | null>(null)
  const currentWorkspace = ref<Workspace | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  const currentRole = computed<WorkspaceRole | null>(
    () => currentWorkspace.value?.pivot?.role ?? null,
  )

  const isWorkspaceOwner = computed(
    () => currentRole.value === WORKSPACE_ROLE.OWNER,
  )

  const isCompanyClient = computed(
    () => currentRole.value === WORKSPACE_ROLE.CLIENT,
  )

  const canInviteClients = computed(() => isWorkspaceOwner.value)
  const canManageWorkspace = computed(() => isWorkspaceOwner.value)
  const isAdmin = computed(() => user.value?.is_admin ?? false)

  function setUser(u: User) {
    user.value = u
    localStorage.setItem('user', JSON.stringify(u))
    if (u.workspaces?.length) {
      setWorkspace(u.workspaces[0])
    }
  }

  function setWorkspace(w: Workspace) {
    currentWorkspace.value = w
    localStorage.setItem('workspace', JSON.stringify(w))
  }

  function clearSession() {
    user.value = null
    currentWorkspace.value = null
    localStorage.removeItem('user')
    localStorage.removeItem('workspace')
  }

  async function fetchSession() {
    isLoading.value = true
    try {
      const { api } = await import('@/api')
      const response = await api.get('/api/auth/user')
      const u: User = response.data.data
      setUser(u)
    } catch {
      clearSession()
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    user,
    currentWorkspace,
    isAuthenticated,
    currentRole,
    isWorkspaceOwner,
    isCompanyClient,
    canInviteClients,
    canManageWorkspace,
    isAdmin,
    setUser,
    setWorkspace,
    clearSession,
    fetchSession,
  }
})
